import moment from "moment";

/**
 * Source unique de vérité pour les dates de naissance d'individus.
 *
 * Deux précisions sont supportées ET stockées telles quelles :
 *   - `DD/MM/YYYY` — date complète
 *   - `MM/YYYY`    — mois + année (DDN partielle, cas très fréquent en parc)
 *
 * Historiquement l'app n'écrivait que `MM/YYYY`, et chaque consommateur
 * re-parsait à sa façon : `getAge` prenait `split("/")[0]` comme mois (faux dès
 * qu'une date complète arrive), la table d'animaux faisait `moment(valeur)`
 * (NaN sur "04/2020"), l'export questionnaire supposait `DD/MM/YYYY`… D'où ce
 * module : tout ce qui lit une DDN passe par `parseBirthdate`.
 *
 * Formats acceptés en LECTURE (données existantes en base incluses) :
 *   "DD/MM/YYYY", "MM/YYYY", "DD/MM/YY" (legacy), "YYYY",
 *   ISO "YYYY-MM-DD" / "YYYY-MM", objet Date, Timestamp Firestore.
 */

export const BIRTHDATE_PRECISION = {
  DAY: "day",
  MONTH: "month",
  YEAR: "year",
};

export const BIRTHDATE_MASKS = {
  [BIRTHDATE_PRECISION.DAY]: "11/11/1111",
  [BIRTHDATE_PRECISION.MONTH]: "11/1111",
};

// Un individu ne vit pas 120 ans : au-delà, c'est une saisie erronée.
const MAX_AGE_YEARS = 120;

const toMoment = (year, month, day) =>
  moment(`${year}-${month || "01"}-${day || "01"}`, "YYYY-MM-DD", true);

/**
 * Découpe une DDN en { year, month, day, precision } sans rien inventer :
 * `day`/`month` valent null quand l'information n'est pas dans la valeur.
 * Renvoie null si la chaîne n'est pas exploitable.
 */
export function splitBirthdate(value) {
  if (!value) return null;

  // Timestamp Firestore / Date : précision jour par construction
  if (typeof value === "object") {
    const date = typeof value.toDate === "function" ? value.toDate() : value;
    if (!(date instanceof Date) || isNaN(date.getTime())) return null;
    const m = moment(date);
    return {
      year: m.format("YYYY"),
      month: m.format("MM"),
      day: m.format("DD"),
      precision: BIRTHDATE_PRECISION.DAY,
    };
  }

  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;

  // ISO / SQL : 2020-04-23, 2020-04
  const iso = raw.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?(?:[T ].*)?$/);
  if (iso) {
    return {
      year: iso[1],
      month: iso[2],
      day: iso[3] || null,
      precision: iso[3] ? BIRTHDATE_PRECISION.DAY : BIRTHDATE_PRECISION.MONTH,
    };
  }

  const parts = raw.split("/");

  // DD/MM/YYYY
  if (parts.length === 3 && parts[2].length === 4) {
    return {
      year: parts[2],
      month: parts[1].padStart(2, "0"),
      day: parts[0].padStart(2, "0"),
      precision: BIRTHDATE_PRECISION.DAY,
    };
  }

  // DD/MM/YY legacy : l'année sur 2 chiffres est trop peu fiable pour garder le
  // jour (c'est déjà la sémantique de _bodCompatibility, qui la réduit à MM/YYYY).
  if (parts.length === 3 && parts[2].length === 2) {
    const m = moment(raw, "DD/MM/YY");
    if (!m.isValid()) return null;
    return {
      year: m.format("YYYY"),
      month: m.format("MM"),
      day: null,
      precision: BIRTHDATE_PRECISION.MONTH,
    };
  }

  // MM/YYYY
  if (parts.length === 2 && parts[1].length === 4) {
    return {
      year: parts[1],
      month: parts[0].padStart(2, "0"),
      day: null,
      precision: BIRTHDATE_PRECISION.MONTH,
    };
  }

  // YYYY seul
  if (/^\d{4}$/.test(raw)) {
    return { year: raw, month: null, day: null, precision: BIRTHDATE_PRECISION.YEAR };
  }

  return null;
}

/**
 * DDN → moment. Les composantes absentes sont ramenées au 1er (janvier) POUR LE
 * CALCUL uniquement — `splitBirthdate` reste la source pour savoir ce qui a
 * réellement été saisi.
 */
export function parseBirthdate(value) {
  const parts = splitBirthdate(value);
  if (!parts) return null;
  const m = toMoment(parts.year, parts.month, parts.day);
  return m.isValid() ? m : null;
}

export function getBirthdatePrecision(value) {
  const parts = splitBirthdate(value);
  return parts ? parts.precision : null;
}

/** Valide une DDN complète : mois 1-12, jour réel du mois, âge plausible. */
export function isBirthdateValid(value) {
  const parts = splitBirthdate(value);
  if (!parts) return false;
  const m = toMoment(parts.year, parts.month, parts.day);
  if (!m.isValid()) return false;
  return moment().diff(m, "years") < MAX_AGE_YEARS;
}

export function isBirthdateInPast(value) {
  const m = parseBirthdate(value);
  return m ? m.isSameOrBefore(moment(), "day") : false;
}

/**
 * Saisie en cours → validité. Un champ VIDE est valide : la date de naissance
 * est optionnelle (beaucoup d'individus arrivent d'un import ZIMS sans DDN
 * connue). Seule une valeur renseignée doit être complète et cohérente.
 */
export function isBirthdateInputAcceptable(value) {
  const raw = (value || "").trim();
  if (!raw) return true;
  return isBirthdateValid(raw) && isBirthdateInPast(raw);
}

/** Chaîne de stockage canonique : "DD/MM/YYYY" ou "MM/YYYY". */
export function formatBirthdate(value) {
  const parts = splitBirthdate(value);
  if (!parts) return "";
  if (parts.precision === BIRTHDATE_PRECISION.DAY) {
    return `${parts.day}/${parts.month}/${parts.year}`;
  }
  if (parts.precision === BIRTHDATE_PRECISION.MONTH) {
    return `${parts.month}/${parts.year}`;
  }
  return parts.year;
}

/**
 * ISO partiel (ex. remonté par ZIMS) → chaîne de saisie AWM.
 * "2020-04-23" → "23/04/2020" ; "2020-04" → "04/2020" ; "2020" → "" (on ne
 * choisit pas un mois à la place de l'utilisateur).
 */
export function birthdateFromISO(iso) {
  const value = (iso || "").trim();
  const match = value.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/);
  if (!match) return "";
  const [, year, month, day] = match;
  if (day) return `${day}/${month}/${year}`;
  if (month) return `${month}/${year}`;
  return "";
}

/**
 * Bascule de précision sans inventer d'information :
 * jour → mois abandonne le jour ; mois → jour vide le champ (choisir un « 01 »
 * arbitraire écrirait une date fausse en base).
 */
export function convertBirthdatePrecision(value, precision) {
  const parts = splitBirthdate(value);
  if (!parts) return "";
  if (precision === BIRTHDATE_PRECISION.MONTH) {
    return parts.month ? `${parts.month}/${parts.year}` : "";
  }
  return parts.day ? `${parts.day}/${parts.month}/${parts.year}` : "";
}

/** Âge en années décimales (analyses, cohortes) — null si non exploitable. */
export function ageInYears(value) {
  const m = parseBirthdate(value);
  return m ? moment().diff(m, "years", true) : null;
}

/** Âge affichable, ex. « 6 ans ». `t` optionnel (repli FR historique). */
export function formatAge(value, t = null) {
  const m = parseBirthdate(value);
  if (!m) return "N/C";
  const age = moment().diff(m, "years");
  if (isNaN(age)) return "N/C";
  return t ? `${age} ${t("common.years", { count: age })}` : `${age} ans`;
}
